import React, { Component } from "react";
import { connect } from "react-redux";
import {
  getGoogleCalendarLogs,
  retryGoogleCalendarLog,
  retryBulkGoogleCalendarLogs,
} from "../../../services/admin";
import Pagination from "../../../components/tablePagination";
import { toast } from "react-toastify";
import moment from "moment-timezone";

const STATUS_LABELS = {
  success: { label: "Success", className: "badge-success" },
  failed: { label: "Failed", className: "badge-failed" },
  pending: { label: "Pending", className: "badge-pending" },
};

const PUSH_TYPE_LABELS = {
  medium_calendar: "Medium Calendar",
  client_calendar: "Client Calendar",
};

function StatBox(props) {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e0e0e0",
        borderRadius: 6,
        padding: "8px 18px",
        minWidth: 90,
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: 22, fontWeight: 700, color: props.color }}>{props.value}</div>
      <div style={{ fontSize: 12, color: "#777" }}>{props.label}</div>
    </div>
  );
}

function formatInTimezone(dateStr, tz) {
  if (!dateStr) return "-";
  var m = moment.utc(dateStr);
  if (tz) {
    m = m.tz(tz);
  }
  return m.format("MMM D, YYYY HH:mm");
}

function getMediumDisplayName(log) {
  if (log.user && log.user.user_preferences && log.user.user_preferences.screen_name) {
    return log.user.user_preferences.screen_name;
  }
  if (log.user) {
    return (log.user.first_name || "") + " " + (log.user.last_name || "");
  }
  return log.user_id || "-";
}

class GoogleCalendarLogs extends Component {
  constructor(props) {
    super(props);
    this.state = {
      logs: [],
      stats: { total: 0, success: 0, failed: 0, pending: 0 },
      totalCount: 0,
      page: 1,
      rowsPerPage: 10,
      retryingIds: [],
      bulkRetrying: false,
      loading: false,
      filters: {
        status: "",
        push_type: "",
        appointment_id: "",
        user_id: "",
        search: "",
        date_from: "",
        date_to: "",
      },
    };
  }

  componentDidMount() {
    this.fetchLogs(1, 10, this.state.filters);
  }

  fetchLogs(currentPage, currentRows, currentFilters) {
    var params = {
      page: currentPage,
      per_page: currentRows,
      status: currentFilters.status,
      push_type: currentFilters.push_type,
      appointment_id: currentFilters.appointment_id,
      user_id: currentFilters.user_id,
      search: currentFilters.search,
      date_from: currentFilters.date_from,
      date_to: currentFilters.date_to,
    };

    this.setState({ loading: true });

    getGoogleCalendarLogs(params)
      .then(function(res) {
        if (res && res.data && res.data.status) {
          var logData = res.data.data.logs;
          var statData = res.data.data.stats;
          this.setState({
            logs: logData.data || [],
            totalCount: logData.total || 0,
            stats: statData || { total: 0, success: 0, failed: 0, pending: 0 },
          });
        }
      }.bind(this))
      .catch(function() {})
      .finally(function() {
        this.setState({ loading: false });
      }.bind(this));
  }

  handleFilterChange(e) {
    var name = e.target.name;
    var value = e.target.value;
    this.setState(function(prev) {
      var updated = Object.assign({}, prev.filters);
      updated[name] = value;
      return { filters: updated };
    });
  }

  applyFilters() {
    this.setState({ page: 1 }, function() {
      this.fetchLogs(1, this.state.rowsPerPage, this.state.filters);
    }.bind(this));
  }

  resetFilters() {
    var empty = { status: "", push_type: "", appointment_id: "", user_id: "", search: "", date_from: "", date_to: "" };
    this.setState({ filters: empty, page: 1 }, function() {
      this.fetchLogs(1, this.state.rowsPerPage, empty);
    }.bind(this));
  }

  getPaginationData(data) {
    this.setState({ page: data.page, rowsPerPage: data.rowsPerPage }, function() {
      this.fetchLogs(data.page, data.rowsPerPage, this.state.filters);
      if (this.tableRef) {
        this.tableRef.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }.bind(this));
  }

  handleRetry(id) {
    this.setState(function(prev) {
      return { retryingIds: prev.retryingIds.concat([id]) };
    });

    retryGoogleCalendarLog(id)
      .then(function(res) {
        if (res && res.data && res.data.status) {
          toast.success((res.data && res.data.message) || "Re-sync queued successfully");
          this.fetchLogs(this.state.page, this.state.rowsPerPage, this.state.filters);
        } else {
          toast.error((res && res.data && res.data.message) || "Re-sync failed");
        }
      }.bind(this))
      .catch(function() { toast.error("Re-sync request failed"); })
      .finally(function() {
        this.setState(function(prev) {
          return { retryingIds: prev.retryingIds.filter(function(i) { return i !== id; }) };
        });
      }.bind(this));
  }

  handleRetryAll() {
    this.setState({ bulkRetrying: true });

    retryBulkGoogleCalendarLogs([])
      .then(function(res) {
        if (res && res.data && res.data.status) {
          toast.success(res.data.message || "Bulk retry queued");
          this.fetchLogs(this.state.page, this.state.rowsPerPage, this.state.filters);
        } else {
          toast.error((res && res.data && res.data.message) || "Bulk retry failed");
        }
      }.bind(this))
      .catch(function() { toast.error("Bulk retry request failed"); })
      .finally(function() {
        this.setState({ bulkRetrying: false });
      }.bind(this));
  }

  render() {
    var logs = this.state.logs;
    var stats = this.state.stats;
    var filters = this.state.filters;
    var totalCount = this.state.totalCount;
    var page = this.state.page;
    var rowsPerPage = this.state.rowsPerPage;
    var retryingIds = this.state.retryingIds;
    var bulkRetrying = this.state.bulkRetrying;
    var loading = this.state.loading;
    var failedCount = stats.failed || 0;

    return (
      <div>
        {/* Stats bar */}
        <div className="d-flex" style={{ gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
          <StatBox label="Total" value={stats.total} color="#555" />
          <StatBox label="Success" value={stats.success} color="#2e7d32" />
          <StatBox label="Failed" value={stats.failed} color="#c62828" />
          <StatBox label="Pending" value={stats.pending} color="#f57c00" />

          {failedCount > 0 && (
            <button
              className="btn btn-danger"
              onClick={this.handleRetryAll.bind(this)}
              disabled={bulkRetrying}
              style={{ marginLeft: "auto" }}
            >
              {bulkRetrying ? "Queuing..." : "Retry All Failed (" + failedCount + ")"}
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="d-flex" style={{ gap: 10, flexWrap: "wrap", marginBottom: 16, alignItems: "flex-end" }}>
          <div>
            <label style={{ fontSize: 12 }}>Medium</label>
            <input
              name="search"
              value={filters.search}
              onChange={this.handleFilterChange.bind(this)}
              onKeyDown={function(e) { if (e.key === "Enter") { this.applyFilters(); } }.bind(this)}
              className="form-control"
              type="text"
              placeholder="Name / email"
              style={{ minWidth: 180 }}
            />
          </div>
          <div>
            <label style={{ fontSize: 12 }}>Status</label>
            <select name="status" value={filters.status} onChange={this.handleFilterChange.bind(this)} className="form-control" style={{ minWidth: 130 }}>
              <option value="">All</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
              <option value="pending">Pending</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12 }}>Push Type</label>
            <select name="push_type" value={filters.push_type} onChange={this.handleFilterChange.bind(this)} className="form-control" style={{ minWidth: 160 }}>
              <option value="">All</option>
              <option value="medium_calendar">Medium Calendar</option>
              <option value="client_calendar">Client Calendar</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12 }}>Appointment ID</label>
            <input
              name="appointment_id"
              value={filters.appointment_id}
              onChange={this.handleFilterChange.bind(this)}
              className="form-control"
              type="number"
              placeholder="Appointment ID"
              style={{ minWidth: 130 }}
            />
          </div>
          <div>
            <label style={{ fontSize: 12 }}>From</label>
            <input name="date_from" value={filters.date_from} onChange={this.handleFilterChange.bind(this)} className="form-control" type="date" />
          </div>
          <div>
            <label style={{ fontSize: 12 }}>To</label>
            <input name="date_to" value={filters.date_to} onChange={this.handleFilterChange.bind(this)} className="form-control" type="date" />
          </div>
          <button className="btn btn-primary" onClick={this.applyFilters.bind(this)}>Apply</button>
          <button className="btn btn-secondary" onClick={this.resetFilters.bind(this)}>Reset</button>
        </div>

        {/* Table */}
        <div className="table-box-medium" ref={function(el) { this.tableRef = el; }.bind(this)}>
          <table className="caseTable">
            <tbody>
              <tr className="dark">
                <th>#</th>
                <th>Appt. ID</th>
                <th>Medium</th>
                <th>Push Type</th>
                <th>Status</th>
                <th>Google Event ID</th>
                <th>Error</th>
                <th>Retries</th>
                <th>Last Retried</th>
                <th>Created At</th>
                <th>Action</th>
              </tr>

              {loading ? (
                <tr>
                  <td colSpan={11} className="text-center py-4">
                    <div className="custom-loader">
                      <div className="loader-dot"></div>
                      <div className="loader-dot"></div>
                      <div className="loader-dot"></div>
                    </div>
                    <div className="loader-text mt-2">Loading logs...</div>
                  </td>
                </tr>
              ) : logs.length === 0 && (
                <tr>
                  <td colSpan={11} style={{ textAlign: "center", padding: 20 }}>
                    No records found
                  </td>
                </tr>
              )}

              {!loading && logs.map(function(log) {
                var badge = STATUS_LABELS[log.status] || { label: log.status, className: "" };
                var isRetrying = retryingIds.indexOf(log.id) !== -1;
                var mediumName = getMediumDisplayName(log);
                var pushTypeLabel = PUSH_TYPE_LABELS[log.push_type] || log.push_type;
                var eventId = log.google_event_id || "-";
                var errorMsg = log.error_message
                  ? (log.error_message.length > 60 ? log.error_message.slice(0, 60) + "..." : log.error_message)
                  : "-";
                var userTz = this.props.adminTimezone || null;
                var lastRetried = formatInTimezone(log.last_retried_at, userTz);

                return (
                  <tr key={log.id}>
                    <td>{log.id}</td>
                    <td>{log.appointment_id}</td>
                    <td>{mediumName}</td>
                    <td>{pushTypeLabel}</td>
                    <td>
                      <span className={"status-badge " + badge.className}>{badge.label}</span>
                    </td>
                    <td style={{ maxWidth: 140, wordBreak: "break-all", fontSize: 12 }}>{eventId}</td>
                    <td style={{ maxWidth: 200, fontSize: 12 }} title={log.error_message || ""}>{errorMsg}</td>
                    <td>{log.retry_count}</td>
                    <td style={{ fontSize: 12 }}>{lastRetried}</td>
                    <td style={{ fontSize: 12 }}>{formatInTimezone(log.created_at, userTz)}</td>
                    <td>
                      {log.status === "failed" && (
                        <button
                          className="btn btn-sm btn-warning"
                          onClick={this.handleRetry.bind(this, log.id)}
                          disabled={isRetrying}
                        >
                          {isRetrying ? "..." : "Retry"}
                        </button>
                      )}
                      {log.status === "success" && (
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={this.handleRetry.bind(this, log.id)}
                          disabled={isRetrying}
                        >
                          {isRetrying ? "..." : "Push again"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              }.bind(this))}
            </tbody>
          </table>
        </div>

        <Pagination
          rowsPerPageOptions={[10, 20, 50]}
          count={totalCount}
          controlled={true}
          page={page}
          rowsPerPage={rowsPerPage}
          getPaginationData={this.getPaginationData.bind(this)}
        />
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    adminTimezone: state.user && state.user.user ? state.user.user.timezone : null,
  };
}

export default connect(mapStateToProps)(GoogleCalendarLogs);
